import { useState, useEffect, useRef, useCallback } from "react";

/**
 * GameHUD v2 — Clean, immersive HUD:
 * - Invisible dynamic joystick on left half (tap-anywhere)
 * - Translucent shot buttons on right
 * - Timing bar for perfect-hit feedback
 * - Top: score + player cards with energy/fatigue rings
 */
export default function GameHUD({
  player, opponent, scoreUI, speed,
  onJoystick, onShot, onDirection,
  gameState = "playing", server = "player",
  fatiguePlayer = 1.0, fatigueOpponent = 1.0,
  timingZone = null,
}) {
  const [selectedDir, setSelectedDir] = useState("center");
  const [selectedShot, setSelectedShot] = useState(null);
  const [timingFeedback, setTimingFeedback] = useState(null); // "perfect"|"good"|"miss"

  const shots = [
    { key: "drive", label: "Drive", color: "#1E90FF" },
    { key: "bandeja", label: "Bandeja", color: "#FFD700" },
    { key: "bolea", label: "Volea", color: "#00FF88" },
    { key: "rulo", label: "Rulo", color: "#FF66CC" },
    { key: "x3", label: "X3", color: "#FF4444" },
  ];

  const directions = [
    { key: "up", label: "↑" },
    { key: "left", label: "←" },
    { key: "right", label: "→" },
    { key: "down", label: "↓" },
    { key: "push", label: "PUSH" },
  ];

  const fireShot = useCallback((key) => {
    setSelectedShot(key);
    const result = onShot?.(key); // returns "perfect" | "good" | "miss" | null
    if (result) {
      setTimingFeedback(result);
      setTimeout(() => setTimingFeedback(null), 600);
    }
    setTimeout(() => setSelectedShot(null), 250);
  }, [onShot]);

  const fireDir = useCallback((key) => {
    setSelectedDir(key);
    onDirection?.(key);
  }, [onDirection]);

  // Keyboard controls
  useEffect(() => {
    const keys = { w: 0, a: 0, s: 0, d: 0 };
    const onKeyDown = (e) => {
      const k = e.key.toLowerCase();
      if (k === "w") keys.w = 1;
      else if (k === "s") keys.s = 1;
      else if (k === "a") keys.a = 1;
      else if (k === "d") keys.d = 1;
      else if (k === " ") fireShot(selectedShot || "drive");
      else if (k === "1") fireShot("drive");
      else if (k === "2") fireShot("bandeja");
      else if (k === "3") fireShot("bolea");
      else if (k === "4") fireShot("rulo");
      else if (k === "5") fireShot("x3");
      else if (k === "arrowup") fireDir("up");
      else if (k === "arrowdown") fireDir("down");
      else if (k === "arrowleft") fireDir("left");
      else if (k === "arrowright") fireDir("right");
      else if (k === "p") fireDir("push");
      onJoystick?.({ x: keys.d - keys.a, y: keys.s - keys.w });
    };
    const onKeyUp = (e) => {
      const k = e.key.toLowerCase();
      if (k === "w") keys.w = 0;
      else if (k === "s") keys.s = 0;
      else if (k === "a") keys.a = 0;
      else if (k === "d") keys.d = 0;
      onJoystick?.({ x: keys.d - keys.a, y: keys.s - keys.w });
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [onJoystick, selectedShot, fireShot, fireDir]);

  return (
    <div className="pointer-events-none absolute inset-0 z-10 select-none" data-testid="game-hud">
      {/* ─── INVISIBLE DYNAMIC JOYSTICK (left half) ─── */}
      <DynamicJoystick onChange={onJoystick} />

      {/* ─── TOP: Score + Player cards with fatigue ring ─── */}
      <div className="absolute top-0 left-0 right-0 flex items-start justify-between px-3 pt-3 gap-2 pointer-events-none">
        <PlayerCardHUD char={player} side="left" fatigue={fatiguePlayer} data-testid="hud-player-left" />
        <div className="bg-black/70 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-2 text-center min-w-[100px]" data-testid="hud-score">
          <div className="font-['Barlow_Condensed'] text-3xl font-black text-white leading-none">
            {scoreUI.p} - {scoreUI.o}
          </div>
          <div className="text-[10px] tracking-widest uppercase text-gray-400 mt-1">
            Set {scoreUI.gp}-{scoreUI.go}
          </div>
        </div>
        <PlayerCardHUD char={opponent} side="right" fatigue={fatigueOpponent} data-testid="hud-player-right" />
      </div>

      {/* Serve hint banner */}
      {gameState === "waiting_serve" && (
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 bg-black/85 backdrop-blur-sm border-2 border-[#CCFF00] rounded-lg px-6 py-3 text-center animate-pulse pointer-events-none" data-testid="hud-serve-hint">
          {server === "player" ? (
            <>
              <div className="font-['Barlow_Condensed'] text-2xl font-black text-[#CCFF00] uppercase tracking-wider">Tu Saque</div>
              <div className="text-[11px] text-white mt-1">Presiona <span className="font-bold text-[#CCFF00]">PUSH</span> o cualquier golpe</div>
            </>
          ) : (
            <>
              <div className="font-['Barlow_Condensed'] text-2xl font-black text-red-400 uppercase tracking-wider">Saque del Rival</div>
              <div className="text-[11px] text-white mt-1">Prepárate...</div>
            </>
          )}
        </div>
      )}

      {/* ─── TIMING FEEDBACK ─── */}
      {timingFeedback && (
        <div className={`absolute top-[42%] left-1/2 -translate-x-1/2 font-['Barlow_Condensed'] text-5xl font-black uppercase tracking-wider pointer-events-none animate-bounce ${timingFeedback === "perfect" ? "text-[#CCFF00]" : timingFeedback === "good" ? "text-amber-300" : "text-red-400"}`}>
          {timingFeedback === "perfect" ? "¡PERFECTO!" : timingFeedback === "good" ? "Bien" : "Caña"}
        </div>
      )}

      {/* ─── TIMING BAR (when ball is approaching) ─── */}
      {timingZone && timingZone.show && (
        <TimingBar progress={timingZone.progress} zoneStart={timingZone.zoneStart} zoneEnd={timingZone.zoneEnd} />
      )}

      {/* ─── BOTTOM CENTER: speed ─── */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm border border-white/10 rounded-md px-4 py-1.5 text-center pointer-events-none" data-testid="hud-speed">
        <div className="text-[9px] tracking-widest uppercase text-gray-400">Velocidad</div>
        <div className="font-['Barlow_Condensed'] text-2xl font-black text-white leading-none">
          {speed} <span className="text-xs text-gray-400 font-normal">KM/H</span>
        </div>
      </div>

      {/* ─── BOTTOM RIGHT: Translucent shot buttons + compact direction pad ─── */}
      <div className="absolute bottom-4 right-4 pointer-events-auto flex flex-col items-end gap-2" data-testid="hud-controls">
        {/* Direction pad — compact, translucent */}
        <div className="flex gap-1 bg-black/30 backdrop-blur-md border border-white/10 rounded-full p-1.5" data-testid="hud-direction-pad">
          {directions.map((d) => {
            const isServePush = d.key === "push" && gameState === "waiting_serve" && server === "player";
            return (
              <button
                key={d.key}
                onClick={() => fireDir(d.key)}
                className={`w-9 h-9 rounded-full text-xs font-bold flex items-center justify-center transition-all ${
                  isServePush ? "bg-[#CCFF00] text-black animate-pulse ring-2 ring-[#CCFF00]" :
                  selectedDir === d.key ? "bg-white/70 text-black" : "bg-white/10 text-white hover:bg-white/25"
                } ${d.key === "push" ? "text-[8px]" : ""}`}
                data-testid={`hud-dir-${d.key}`}
              >
                {d.label}
              </button>
            );
          })}
        </div>
        {/* Shot buttons — translucent vertical stack */}
        <div className="flex flex-col gap-1.5" data-testid="hud-shots">
          {shots.map((s) => (
            <button
              key={s.key}
              onClick={() => fireShot(s.key)}
              className="w-20 px-3 py-2 rounded-full border-2 font-bold uppercase tracking-wider text-[11px] transition-transform backdrop-blur-md"
              style={{
                borderColor: `${s.color}`,
                color: selectedShot === s.key ? "#000" : s.color,
                background: selectedShot === s.key ? s.color : `${s.color}22`,
                transform: selectedShot === s.key ? "scale(1.1)" : "scale(1)",
                boxShadow: selectedShot === s.key ? `0 0 20px ${s.color}99` : "none",
              }}
              data-testid={`hud-shot-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───

function PlayerCardHUD({ char, side, fatigue = 1.0 }) {
  if (!char) return null;
  const color = char.accent_color || (side === "left" ? "#1E90FF" : "#FF4444");
  // Fatigue ring: 1.0 (full) → 0 (exhausted)
  const ringPct = Math.max(0, Math.min(1, fatigue));
  const circumference = 2 * Math.PI * 18;
  const dash = circumference * ringPct;
  return (
    <div className="flex items-center gap-2 bg-black/70 backdrop-blur-sm border-2 rounded-lg p-1.5 pr-3 max-w-[200px]" style={{ borderColor: color, boxShadow: `0 0 12px ${color}66` }}>
      {side === "right" && <PlayerInfo char={char} side="right" color={color} />}
      <div className="relative w-11 h-11 shrink-0">
        {/* Fatigue ring */}
        <svg className="absolute inset-0 -rotate-90" viewBox="0 0 44 44">
          <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="2.5" />
          <circle cx="22" cy="22" r="18" fill="none" stroke={ringPct > 0.5 ? color : ringPct > 0.25 ? "#FFA500" : "#FF4444"} strokeWidth="2.5" strokeDasharray={`${dash} ${circumference}`} strokeLinecap="round" />
        </svg>
        {/* Portrait */}
        <div className="absolute inset-1 rounded overflow-hidden">
          {char.portrait_url ? (
            <img src={char.portrait_url} alt={char.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gray-700" />
          )}
        </div>
      </div>
      {side === "left" && <PlayerInfo char={char} side="left" color={color} />}
    </div>
  );
}

function PlayerInfo({ char, side, color }) {
  const parts = (char.name || "").split(" ");
  return (
    <div className={side === "right" ? "text-right" : "text-left"}>
      <div className="font-['Barlow_Condensed'] uppercase font-bold text-[11px] tracking-wider truncate max-w-[110px]" style={{ color }}>
        {parts[0]} {parts[1] || ""}
      </div>
      <div className="flex gap-1.5 mt-0.5 text-[8px] tracking-wider" style={{ flexDirection: side === "right" ? "row-reverse" : "row" }}>
        <span className="text-gray-400">P:<span className="text-white font-bold ml-0.5">{char.stats?.power || "-"}</span></span>
        <span className="text-gray-400">V:<span className="text-white font-bold ml-0.5">{char.stats?.speed || "-"}</span></span>
        <span className="text-gray-400">C:<span className="text-white font-bold ml-0.5">{char.stats?.control || "-"}</span></span>
      </div>
    </div>
  );
}

// ─── Timing bar (renders horizontal bar with moving indicator + green zone) ───
function TimingBar({ progress = 0, zoneStart = 0.4, zoneEnd = 0.6 }) {
  return (
    <div className="absolute bottom-32 left-1/2 -translate-x-1/2 w-56 h-3 bg-black/60 backdrop-blur-sm border border-white/30 rounded-full overflow-hidden pointer-events-none" data-testid="timing-bar">
      {/* Green optimal zone */}
      <div
        className="absolute top-0 bottom-0 bg-[#CCFF00]"
        style={{ left: `${zoneStart * 100}%`, width: `${(zoneEnd - zoneStart) * 100}%` }}
      />
      {/* Yellow good zones (around green) */}
      <div
        className="absolute top-0 bottom-0 bg-amber-300/40"
        style={{ left: `${Math.max(0, zoneStart - 0.1) * 100}%`, width: `${Math.min(0.1, zoneStart) * 100}%` }}
      />
      <div
        className="absolute top-0 bottom-0 bg-amber-300/40"
        style={{ left: `${zoneEnd * 100}%`, width: `${Math.min(0.1, 1 - zoneEnd) * 100}%` }}
      />
      {/* Moving indicator */}
      <div
        className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
        style={{ left: `calc(${Math.max(0, Math.min(1, progress)) * 100}% - 2px)` }}
      />
    </div>
  );
}

// ─── Dynamic invisible joystick (left half) ───
function DynamicJoystick({ onChange }) {
  const areaRef = useRef(null);
  const [base, setBase] = useState(null); // {x, y} pixel center when active
  const [knob, setKnob] = useState({ x: 0, y: 0 });
  const activeRef = useRef(false);

  const maxRadius = 60;

  const handleStart = useCallback((clientX, clientY) => {
    setBase({ x: clientX, y: clientY });
    setKnob({ x: 0, y: 0 });
    activeRef.current = true;
    onChange?.({ x: 0, y: 0 });
  }, [onChange]);

  const handleMove = useCallback((clientX, clientY) => {
    if (!activeRef.current || !base) return;
    const dx = clientX - base.x;
    const dy = clientY - base.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const clamp = Math.min(dist, maxRadius);
    const ang = Math.atan2(dy, dx);
    const kx = Math.cos(ang) * clamp;
    const ky = Math.sin(ang) * clamp;
    setKnob({ x: kx, y: ky });
    onChange?.({ x: kx / maxRadius, y: ky / maxRadius });
  }, [base, onChange]);

  const handleEnd = useCallback(() => {
    activeRef.current = false;
    setBase(null);
    setKnob({ x: 0, y: 0 });
    onChange?.({ x: 0, y: 0 });
  }, [onChange]);

  useEffect(() => {
    const onPointerMove = (e) => handleMove(e.clientX, e.clientY);
    const onPointerUp = () => handleEnd();
    const onTouchMove = (e) => { if (e.touches[0]) handleMove(e.touches[0].clientX, e.touches[0].clientY); };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [handleMove, handleEnd]);

  return (
    <div
      ref={areaRef}
      onPointerDown={(e) => { e.preventDefault(); handleStart(e.clientX, e.clientY); }}
      className="absolute left-0 top-0 bottom-0 w-1/2 pointer-events-auto touch-none"
      style={{ background: "transparent" }}
      data-testid="hud-joystick-area"
    >
      {/* Visible only when active */}
      {base && (
        <>
          <div
            className="absolute w-28 h-28 rounded-full border-2 border-white/40 pointer-events-none"
            style={{ left: base.x - 56, top: base.y - 56, background: "rgba(0,0,0,0.25)", backdropFilter: "blur(6px)" }}
          />
          <div
            className="absolute w-14 h-14 rounded-full bg-white/80 pointer-events-none shadow-xl"
            style={{ left: base.x - 28 + knob.x, top: base.y - 28 + knob.y }}
          />
        </>
      )}
    </div>
  );
}
